// window.WA — публичный JS API сайтов newgen-семейства (newgen, newgen_v2,
// wola, ola) для HTML/JS, вставляемого через MCP (site_mcp_menu_*, html-блоки
// статей). Такой код исполняется ДО загрузки пака, поэтому снаружи работает
// паттерн очереди (как у gtag):
//   (window.WAQ = window.WAQ || []).push(function (WA) { ... });
// Очередь разбирает initializer после installWA; поздние push выполняются сразу.
//
// Денежные суммы (totalSum и всё внутри raw) — в КОПЕЙКАХ, как их отдаёт v3 API.

import { api, apiState } from '../api/instance';
import { catalogService } from '../stores/catalogStore/service';
import { getSiteParams } from '../shared/siteParams';

// ---------------------------------------------------------------------------
// Мобильное меню: тот же DOM-код, что в делегате initMobileMenuToggle
// (initializer.js) — #mobile-menu.active + body.overflowed. Экспортируются,
// чтобы делегат и WA.ui использовали одну реализацию.
export const openMobileMenu = () => {
  const menu = document.getElementById('mobile-menu');
  if (menu) menu.classList.add('active');
  document.body.classList.add('overflowed');
};

export const closeMobileMenu = () => {
  const menu = document.getElementById('mobile-menu');
  if (menu) menu.classList.remove('active');
  document.body.classList.remove('overflowed');
};

export const toggleMobileMenu = () => {
  const menu = document.getElementById('mobile-menu');
  if (menu) menu.classList.toggle('active');
  document.body.classList.toggle('overflowed');
};

// ---------------------------------------------------------------------------
// Сетка каталога. У newgen/newgen_v2 колонки задаёт ng-catalog-cols-N на .row
// с карточками .item-thumb; у wola/ola — ngg-catalog-grid--cols-N на
// .ngg-catalog-grid. Контейнер сам говорит, какой префикс ему нужен.
const COLS_CLASS_RE = /^(ng-catalog-cols|ngg-catalog-grid--cols)-\d+$/;

const setCatalogColsInDom = (n) => {
  const cols = Math.min(6, Math.max(2, parseInt(n, 10) || 2));
  const containers = [];
  document.querySelectorAll('.ngg-catalog-grid').forEach((el) => containers.push(el));
  document.querySelectorAll('.row').forEach((el) => {
    if (el.classList.contains('ngg-catalog-grid')) return; // уже добавлен
    if (el.querySelector('.item-thumb')) containers.push(el);
  });
  // Сетки на странице нет — молча no-op.
  containers.forEach((el) => {
    Array.prototype.slice.call(el.classList).forEach((cls) => {
      if (COLS_CLASS_RE.test(cls)) el.classList.remove(cls);
    });
    const prefix = el.classList.contains('ngg-catalog-grid') ? 'ngg-catalog-grid--cols' : 'ng-catalog-cols';
    el.classList.add(`${prefix}-${cols}`);
  });
};

// <style data-wa-style="id"> в <head>. Ищем перебором, а не селектором —
// чтобы произвольный id не требовал экранирования в querySelector.
const findWaStyle = (id) => {
  const styles = document.head.querySelectorAll('style[data-wa-style]');
  for (let i = 0; i < styles.length; i += 1) {
    if (styles[i].getAttribute('data-wa-style') === String(id)) return styles[i];
  }
  return null;
};

// ---------------------------------------------------------------------------
// installWA(rootStore, mountIn) → { WA, markReady }.
// mountIn(container) — функция из initializer, домонтирующая React-виджеты
// в [data-react]-узлах контейнера (для WA.ui.mount).
export const installWA = (rootStore, mountIn) => {
  const cartStore = rootStore.cartStore;
  const authStore = rootStore.authStore;

  // ---------- внутренняя шина событий: имя → Set колбэков
  const listeners = new Map();

  const emit = (event, payload) => {
    const set = listeners.get(event);
    if (!set) return;
    // копия — колбэк может отписать себя во время обхода
    // (не slice.call: у Set нет .length, slice вернул бы пустой массив)
    const copy = [];
    set.forEach((cb) => copy.push(cb));
    copy.forEach((cb) => {
      try { cb(payload); } catch (e) { console.error(`[WA] "${event}" listener failed`, e); }
    });
  };

  const off = (event, cb) => {
    const set = listeners.get(event);
    if (set) set.delete(cb);
  };

  const on = (event, cb) => {
    if (typeof cb !== 'function') return () => {};
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event).add(cb);
    // поздний подписчик на 'ready' — событие уже было, вызываем сразу
    // (асинхронно, как WA.ready)
    if (event === 'ready' && isReady) setTimeout(() => cb(WA), 0);
    return () => off(event, cb);
  };

  // ---------- снапшоты
  const cartSnapshot = () => {
    const raw = cartStore.cart || {};
    return {
      items: (cartStore.items || []).map((i) => Object.assign({}, i, {
        itemId: i.item_id,
        quantity: i.quantity || 0,
      })),
      count: cartStore.count,
      totalQuantity: raw.total_quantity || 0,
      totalSum: cartStore.totalSum, // копейки
      raw,
    };
  };

  const userSnapshot = () => ({
    isAuthenticated: !!authStore.isAuthenticated,
    // client_info для гостя — {} (так отдаёт сериализатор), а по контракту
    // client должен быть null для гостя.
    client: authStore.isAuthenticated ? (authStore.clientInfo || null) : null,
  });

  // try/catch: исключение из подписчика rxjs-5 subject.next() вылетает в
  // код стора и сломало бы обновление корзины для всей страницы.
  const documentEvent = (name, detail) => {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (e) {
      console.error(`[WA] dispatch ${name} failed`, e);
    }
  };

  // Подписки на сторы — один раз при install, живут всю жизнь страницы.
  cartStore.subject.subscribe(() => {
    const snap = cartSnapshot();
    emit('cart:changed', snap);
    documentEvent('wa:cart:changed', snap);
  });
  authStore.subject.subscribe(() => {
    const snap = userSnapshot();
    emit('auth:changed', snap);
    documentEvent('wa:auth:changed', snap);
  });

  // ---------- ready
  let isReady = false;
  const readyQueue = [];

  const ready = (cb) => {
    if (typeof cb !== 'function') return;
    if (isReady) setTimeout(() => cb(WA), 0);
    else readyQueue.push(cb);
  };

  // ---------- catalog: HTTP 400 = у компании выключен мобильный API
  // (is_mobile_app_enabled=false) — reject с понятной ошибкой.
  const catalogError = (err) => {
    if (err && err.status === 400) {
      const e = new Error('WA.catalog: catalog API is disabled for this company (is_mobile_app_enabled=false)');
      e.status = 400;
      e.original = err;
      throw e;
    }
    throw err;
  };

  const WA = {
    version: '1',

    ready,
    on,
    off,

    cart: {
      // set — АБСОЛЮТНОЕ количество: v3 update_cart_item именно устанавливает
      // quantity, а не прибавляет. opts: { priceGroupId, toppingIds }.
      set(itemId, qty, opts) {
        const o = opts || {};
        const payload = { item_id: itemId, quantity: qty };
        if (o.priceGroupId != null) payload.price_group_id = o.priceGroupId;
        if (o.toppingIds != null) payload.topping_ids = o.toppingIds;
        return Promise.resolve(cartStore.addProductToCart(payload)).then(cartSnapshot);
      },
      add(itemId, delta, opts) {
        const d = delta == null ? 1 : delta;
        const existing = (cartStore.items || []).find((i) => String(i.item_id) === String(itemId));
        const current = existing ? (existing.quantity || 0) : 0;
        return WA.cart.set(itemId, current + d, opts);
      },
      remove(itemId) {
        return Promise.resolve(cartStore.removeProductFromCart(itemId)).then(cartSnapshot);
      },
      clear() {
        return Promise.resolve(cartStore.cleanCart()).then(cartSnapshot);
      },
      repeat(orderId) {
        return Promise.resolve(cartStore.repeatOrder(orderId)).then(cartSnapshot);
      },
      // Синхронный снапшот. Суммы — в копейках.
      get: cartSnapshot,
    },

    ui: {
      openLogin() { authStore.setLoginDialogOpen(true); },
      closeLogin() { authStore.setLoginDialogOpen(false); },
      openCityDialog() { authStore.setCityBranchDialogOpen(true); },
      // authStore.setCity сам страницу НЕ перезагружает — в UI это делает
      // CityBranchDialog после успешного ответа. Повторяем его поведение:
      // контент страницы (каталог, цены) зависит от филиала.
      setCity(id) {
        return Promise.resolve(authStore.setCity(id)).then(() => { window.location.reload(); });
      },
      openMobileMenu,
      closeMobileMenu,
      toggleMobileMenu,
      // Домонтировать React-виджеты в динамически вставленных узлах
      // [data-react] (например, <div data-react="ToCart" data-params='...'>
      // из MCP-HTML). Уже смонтированные узлы пропускаются.
      mount(container) {
        if (typeof mountIn === 'function') mountIn(container || document);
      },
    },

    user: {
      get: userSnapshot,
      logout() { return Promise.resolve(authStore.logout()); },
    },

    catalog: {
      get(params) {
        const p = params || {};
        return catalogService.getCatalog({ categoryId: p.categoryId, q: p.q }).catch(catalogError);
      },
      item(id) {
        return catalogService.getProductDetails(id).catch(catalogError);
      },
      search(q) {
        return catalogService.search(q).catch(catalogError);
      },
    },

    site: {
      params() {
        return Object.assign({}, getSiteParams(), { companyId: apiState.getCompanyId() });
      },
    },

    theme: {
      // В body-классе имени темы нет, поэтому отдаём site_params.design
      // (site_design_type компании).
      get name() { return getSiteParams().design || null; },
      // Именно так меняется внешний вид (размеры карточек и т.п.): CSS-файлы
      // тем собраны через precss/postcss-custom-properties, который ИНЛАЙНИТ
      // var() на этапе сборки — переопределение CSS-переменных в рантайме не
      // сработает, нужен обычный CSS с перебивающими правилами.
      setStyle(id, css) {
        let el = findWaStyle(id);
        if (!el) {
          el = document.createElement('style');
          el.setAttribute('data-wa-style', String(id));
          document.head.appendChild(el);
        }
        el.textContent = css == null ? '' : String(css);
      },
      removeStyle(id) {
        const el = findWaStyle(id);
        if (el) el.parentNode.removeChild(el);
      },
      setCatalogCols: setCatalogColsInDom,
    },

    // Тонкая обёртка над v3-инстансом; пути — относительно /api/:company_id/
    // (например, WA.http.post('v3/catalog', {...})). mobile-token и CSRF
    // подставляются автоматически.
    http: {
      get(path, params) { return api.get(path, params); },
      post(path, body) { return api.post(path, body); },
      put(path, body) { return api.put(path, body); },
      del(path) { return api.del(path); },
    },
  };

  // Вызывается initializer-ом после bootstrap + mountAll (в обоих исходах
  // bootstrap): выполняет очередь WA.ready, шлёт 'ready' и 'wa:ready'.
  const markReady = () => {
    if (isReady) return;
    isReady = true;
    readyQueue.splice(0).forEach((cb) => {
      try { cb(WA); } catch (e) { console.error('[WA] ready callback failed', e); }
    });
    emit('ready', WA);
    documentEvent('wa:ready', { WA });
  };

  return { WA, markReady };
};

// Разбор очереди window.WAQ: выполнить накопленные колбэки и подменить push,
// чтобы последующие push выполнялись сразу (паттерн gtag).
export const drainWAQ = (WA) => {
  const run = (cb) => {
    if (typeof cb !== 'function') return;
    try { cb(WA); } catch (e) { console.error('[WA] WAQ callback failed', e); }
  };
  const waq = (window.WAQ = window.WAQ || []);
  while (waq.length) run(waq.shift());
  waq.push = (cb) => { run(cb); return waq.length; };
};



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/wa/index.js